import { formatURL } from "shelving";
import { Icon, MediaImage, Page, PathButton } from "shared/react";
import { POSTS_MAX, getProfilePath } from "shared/schema";
import { pluralizeCompactQuantity } from "shared/util/number";
import { EmbedMarkup } from "../misc/EmbedMarkup.js";
import { PostGrid } from "../post/PostGrid.js";
import { useProfileAllowedPrivacyTypes } from "../util/privacy.js";
import { useRewardStyle } from "../util/reward.js";
import { USER } from "../util/user.js";
import { FollowButton } from "./FollowButton.js";
import { FollowsYouNotice } from "./FollowsYouNotice.js";
import { useProfileContext } from "./ProfileContext.js";
import { ProfileNavigation } from "./ProfileNavigation.js";
import { ProfileStats } from "./ProfileStats.js";

export function ProfilePage(): React.ReactElement {
	const profile = useProfileContext();
	const { id: uid, username, name, background, about, location, website, photo, following, followers } = profile;
	const path = getProfilePath(profile);
	const creator = `profile:${uid}` as const;
	const privacy = useProfileAllowedPrivacyTypes(uid);
	const isMe = uid === USER.data.uid;
	return (
		<Page path={path}>
			<header className="panel pad-none" style={useRewardStyle(background)}>
				<div className="pad-3x">
					<figure className="photo size-5x">{photo ? <MediaImage path={photo} /> : <Icon icon="profile" />}</figure>
					<h1>
						{name ? (
							<>
								<strong>{name}</strong> <small>{`@${username}`}</small>
							</>
						) : (
							<strong>{`@${username}`}</strong>
						)}
					</h1>
					<p className="flex-row flex-center gap-xsmall">
						<ProfileStats {...profile} />
					</p>
					<p className="flex-row flex-center gap-xsmall size-small">
						<PathButton path="./following" mods="button-quiet">
							{pluralizeCompactQuantity(following, "following", "following")}
						</PathButton>
						<PathButton path="./followers" mods="button-quiet">
							{pluralizeCompactQuantity(followers, "follower", "followers")}
						</PathButton>
					</p>
					{!isMe ? <FollowsYouNotice follower={uid} /> : null}
				</div>
				{isMe ? (
					<PathButton path="/settings/profile" mods="button-quiet pin-top pin-right">
						Edit
					</PathButton>
				) : (
					<FollowButton followed={uid} mods="button-quiet pin-top pin-right" />
				)}
				<ProfileNavigation uid={uid} />
			</header>
			{about || location || website ? (
				<section>
					{about ? <EmbedMarkup>{about}</EmbedMarkup> : null}
					{location || website ? (
						<p className="flex-row flex-center gap-xsmall size-small">
							{location ? (
								<a href={`https://www.google.com/maps?q=${encodeURIComponent(location)}`} className="button">
									{location}
								</a>
							) : null}
							{website ? (
								<a href={website} className="button">
									{formatURL(website)}
								</a>
							) : null}
						</p>
					) : null}
				</section>
			) : null}
			{isMe ? (
				<section>
					<PathButton path="/posts/new">Create post</PathButton>
				</section>
			) : null}
			<PostGrid //
				mods="space-none width-wide grid-cols-3"
				query={{
					creator,
					privacy,
					// privacy: "approved",
					$order: "!date",
					$limit: POSTS_MAX,
				}}
				empty="No victory posts yet"
				detect
			/>
		</Page>
	);
}
